import apiClient from '@app/config/axios.config'
import { Endpoint } from '@app/config/endpoints'
import { logger } from '@app/utils/logger'
import type { paths } from '@src/types/api'
import {
  ApiDocument,
  DocumentsListResponse,
  DocumentsQuery,
  EvidenceDocument,
  TagFacet,
} from '../documents.types'
import { DEFAULT_QUERY } from '../documents.constants'
import { fileSizeBandToRange } from '../documents.utils'

export type CreateDocumentPayload = NonNullable<
  paths['/evidence/documents']['post']['requestBody']
>['content']['application/json']
export type UpdateDocumentPayload = NonNullable<
  paths['/evidence/documents/{document_id}']['patch']['requestBody']
>['content']['application/json']
export type UpdateDocumentPermissionsPayload = NonNullable<
  paths['/evidence/documents/{document_id}/permissions']['patch']['requestBody']
>['content']['application/json']

function mapApiDocument(doc: ApiDocument): EvidenceDocument {
  return {
    id: doc.uuid,
    title: doc.title,
    descriptionMd: doc.description_md,
    category: doc.category,
    ownerId: doc.owner,
    ownerEmail: doc.owner_email,
    source: doc.source,
    organisation: doc.organisation,
    publishedAt: doc.published_at,
    uploadedAt: doc.created_at,
    notesMd: doc.notes_md,
    tags: doc.tags,
    georeference: { type: doc.georeference_type, geometry: doc.geometry },
    visibility: doc.visibility,
    file: {
      originalFilename: doc.original_filename,
      mimeType: doc.mime_type,
      sizeBytes: doc.size_bytes,
    },
    collectionId: doc.collection_id,
  }
}

function buildDocumentsParams(query: DocumentsQuery): Record<string, string | number | boolean> {
  const { sizeMin, sizeMax } = fileSizeBandToRange(query.fileSizeBand)
  const params: Record<string, string | number | boolean> = {
    page: query.page,
    page_size: query.pageSize,
    sort_by: query.sort.field,
    sort_direction: query.sort.direction,
  }

  if (query.search.trim()) params.search = query.search.trim()
  if (query.category !== 'all') params.category = query.category
  if (query.tags.length > 0) params.tags = query.tags.join(',')
  if (query.organisation.trim()) params.organisation = query.organisation.trim()
  if (query.source.trim()) params.source = query.source.trim()
  if (query.visibility !== 'all') params.visibility = query.visibility
  if (query.georeferencedOnly) params.georeferenced_only = true
  if (sizeMin !== undefined) params.size_min = sizeMin
  if (sizeMax !== undefined) params.size_max = sizeMax
  if (query.uploadedFrom) params.uploaded_from = query.uploadedFrom
  if (query.uploadedTo) params.uploaded_to = query.uploadedTo
  if (query.publishedFrom) params.published_from = query.publishedFrom
  if (query.publishedTo) params.published_to = query.publishedTo
  if (query.collectionId) params.collection_id = query.collectionId

  return params
}

export async function fetchDocuments(query: DocumentsQuery): Promise<DocumentsListResponse> {
  try {
    const response = await apiClient.get<
      paths['/evidence/documents']['get']['responses'][200]['content']['application/json']
    >(Endpoint.documents, {
      params: buildDocumentsParams(query),
    })
    return {
      total_count: response.data.total_count,
      page_count: response.data.page_count,
      documents: response.data.documents.map(mapApiDocument),
      tags: response.data.tags,
    }
  } catch (error) {
    logger.error('Error fetching documents:', error)
    throw error
  }
}

// Reuses the documents list endpoint's tags facet, unfiltered, just to get the full set of
// existing tag names for the upload/edit form's tag picker — page_size:1 keeps it cheap.
export async function fetchAllTags(): Promise<TagFacet[]> {
  const { tags } = await fetchDocuments({ ...DEFAULT_QUERY, pageSize: 1 })
  return tags
}

export async function createDocument(payload: CreateDocumentPayload): Promise<EvidenceDocument> {
  try {
    const response = await apiClient.post<ApiDocument>(Endpoint.documents, payload)
    return mapApiDocument(response.data)
  } catch (error) {
    logger.error('Error creating document:', error)
    throw error
  }
}

export async function updateDocument(
  id: string,
  payload: UpdateDocumentPayload,
): Promise<EvidenceDocument> {
  try {
    const response = await apiClient.patch<ApiDocument>(`${Endpoint.documents}/${id}`, payload)
    return mapApiDocument(response.data)
  } catch (error) {
    logger.error('Error updating document:', error)
    throw error
  }
}

export async function updateDocumentPermissions(
  id: string,
  payload: UpdateDocumentPermissionsPayload,
): Promise<void> {
  try {
    await apiClient.patch<void>(`${Endpoint.documents}/${id}/permissions`, payload)
  } catch (error) {
    logger.error('Error updating document permissions:', error)
    throw error
  }
}

export async function getDocumentPermissions(
  id: string,
): Promise<UpdateDocumentPermissionsPayload> {
  try {
    const { data } = await apiClient.get<
      paths['/evidence/documents/{document_id}/permissions']['get']['responses'][200]['content']['application/json']
    >(`${Endpoint.documents}/${id}/permissions`)
    return data
  } catch (error) {
    logger.error('Error fetching document permissions:', error)
    throw error
  }
}

export async function getDocumentPreviewUrl(id: string): Promise<{ url: string }> {
  try {
    const { data } = await apiClient.get<{ url: string }>(`${Endpoint.documents}/${id}/preview`)
    return data
  } catch (error) {
    logger.error('Error fetching document preview URL:', error)
    throw error
  }
}

export async function getDocument(id: string): Promise<EvidenceDocument> {
  try {
    const response = await apiClient.get<
      paths['/evidence/documents/{document_id}']['get']['responses'][200]['content']['application/json']
    >(`${Endpoint.documents}/${id}`)
    return mapApiDocument(response.data)
  } catch (error) {
    logger.error('Error fetching document:', error)
    throw error
  }
}

export async function deleteDocument(id: string): Promise<void> {
  try {
    await apiClient.delete<void>(`${Endpoint.documents}/${id}`)
  } catch (error) {
    logger.error('Error deleting document:', error)
    throw error
  }
}

// Note GET /evidence/documents/:id/download returns { url } rather than a 302
// redirect, since a credentialed apiClient request can never satisfy a presigned URL's CORS
// rules. We feed that URL to the browser via an anchor click rather than fetching the
// bytes ourselves. This triggers the normal browser download behaviour cleanly.
export async function downloadDocument(id: string, filename: string): Promise<void> {
  try {
    const { data } = await apiClient.get<{ url: string }>(`${Endpoint.documents}/${id}/download`)
    const link = document.createElement('a')
    link.href = data.url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
  } catch (error) {
    logger.error('Error downloading document:', error)
    throw error
  }
}
